import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StreamState, { type StreamStateValue } from "./StreamState";
import type { PlatformSummary } from "../types/platform";
import type { StreamSummary } from "../types/streamSummary";
import { getPlatformIcon } from "./PlatformIcons";
import { normalizeTags } from "../utils/tags";

type PlatformCardsProps = {
  platforms: PlatformSummary[];
  streamsByPlatform: Map<string, StreamSummary[]>;
  isStreamsLoading: boolean;
  streamsErrorMessage: string | null;
  getStreamState: (streamId: string) => StreamStateValue | undefined;
  canManagePlatforms: boolean;
  onEdit: (platform: PlatformSummary) => void;
  onDelete: (platform: PlatformSummary) => void;
  fallbackPlatformColor: string;
};

const PlatformCards = ({
  platforms,
  streamsByPlatform,
  isStreamsLoading,
  streamsErrorMessage,
  getStreamState,
  canManagePlatforms,
  onEdit,
  onDelete,
  fallbackPlatformColor
}: PlatformCardsProps) => {
  if (platforms.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={2} alignItems="stretch">
      {platforms.map((platform) => {
        const platformDescription = platform.description?.trim() ?? "";
        const platformStreams = streamsByPlatform.get(platform.id) ?? [];
        const hasDescription = platformDescription.length > 0;
        const listMarginTop = hasDescription ? 1.5 : 1;
        const platformColor = platform.color?.trim() || fallbackPlatformColor;
        const platformTags = normalizeTags(platform.tags);
        const platformModel = platform.model?.trim() ?? "";
        const typeLabel = platform.type.toUpperCase();
        const typeChipLabel = platformModel ? `${typeLabel} · ${platformModel}` : typeLabel;
        const tagsLabel = platformTags.join(", ");
        const PlatformIcon = getPlatformIcon(platform.type);

        return (
          <Grid key={platform.id} size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: "flex" }}>
            <Card
              variant="outlined"
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                minWidth: { md: 240, lg: 280 }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                  <Typography variant="h6">{platform.name}</Typography>
                  <Stack spacing={0.75} alignItems="flex-end">
                    <Chip
                      icon={<PlatformIcon fontSize="small" />}
                      label={typeChipLabel}
                      variant="outlined"
                      size="small"
                      sx={{
                        "& .MuiChip-icon": { marginLeft: "4px", color: platformColor },
                        borderColor: platformColor,
                        color: platformColor
                      }}
                    />
                  </Stack>
                </Box>
                <Box display="flex" flexDirection="column" gap={0.25} sx={{ mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Model: {platformModel}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tags: {tagsLabel}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, minHeight: "1.5em", whiteSpace: "pre-wrap" }}
                >
                  {platformDescription}
                </Typography>
                {streamsErrorMessage ? (
                  <Typography variant="body2" color="error" sx={{ mt: listMarginTop }}>
                    {streamsErrorMessage}
                  </Typography>
                ) : isStreamsLoading ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: listMarginTop }}>
                    Loading streams...
                  </Typography>
                ) : (
                  <Stack spacing={0.75} sx={{ mt: listMarginTop }}>
                    {platformStreams.length > 0 ? (
                      platformStreams.map((stream) => {
                        const streamState = getStreamState(stream.id);
                        const streamDescription = stream.description?.trim() ?? "";
                        const tooltipTitle = streamDescription || "No description provided.";
                        return (
                          <Box key={stream.id} display="flex" alignItems="center" gap={1}>
                            <StreamState state={streamState} />
                            <Tooltip title={tooltipTitle} placement="top" arrow disableInteractive>
                              <Typography variant="body2">{stream.sensor}</Typography>
                            </Tooltip>
                          </Box>
                        );
                      })
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No streams assigned.
                      </Typography>
                    )}
                  </Stack>
                )}
              </CardContent>
              {canManagePlatforms ? (
                <CardActions sx={{ mt: "auto", justifyContent: "center", gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => onEdit(platform)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => onDelete(platform)}
                  >
                    Delete
                  </Button>
                </CardActions>
              ) : null}
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default PlatformCards;
